import { type FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  formatUsdcBaseUnits,
  parseUsdcToBaseUnits,
  InvalidAmountError,
  LISTING_CATEGORY_LABELS,
  ACCOUNT_PLATFORM_LABELS,
} from "@hybrid-escrow/shared";
import { useListing } from "../hooks/useListings";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { useSellerReviews } from "../hooks/useReviews";
import { useStartConversation } from "../hooks/useConversations";
import { apiClient } from "../lib/api-client";
import { Money } from "../components/Money";
import { StarRatingDisplay } from "../components/StarRating";
import { SellerBadges } from "../components/SellerBadges";
import { ErrorBanner, LoadingState, describeError } from "../components/States";
import { ListingCover } from "../components/ListingCover";
import { formatDateTime } from "../lib/format";
import { usePageMeta, useJsonLd, breadcrumbJsonLd, SITE_URL } from "../lib/seo";

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

export function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: listing, isLoading, isError, error, refetch } = useListing(id);
  const { isSignedIn, principal } = useAuthPrincipal();
  const { data: reviews } = useSellerReviews(listing?.sellerId);
  const startConversation = useStartConversation();
  const [messageError, setMessageError] = useState<string | null>(null);
  const watchListing = useMutation({ mutationFn: (listingId: string) => apiClient.watchListing(listingId) });

  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerAmountInput, setOfferAmountInput] = useState("");
  const [offerFormError, setOfferFormError] = useState<string | null>(null);
  const makeOffer = useMutation({
    mutationFn: (amountBaseUnits: string) =>
      apiClient.createOffer(id as string, { amountBaseUnits }),
  });

  function handleOfferSubmit(e: FormEvent) {
    e.preventDefault();
    setOfferFormError(null);
    let amount: bigint;
    try {
      amount = parseUsdcToBaseUnits(offerAmountInput.trim());
    } catch (err) {
      setOfferFormError(err instanceof InvalidAmountError ? err.message : "Enter a valid USDC amount.");
      return;
    }
    makeOffer.mutate(amount.toString());
  }

  usePageMeta({
    title: listing?.title ?? "Listing",
    description: listing
      ? truncate(listing.description, 160)
      : "Buy and sell digital goods with onchain USDC on MarketOnBase.",
    path: `/listings/${id ?? ""}`,
    // A 404/gone listing (deleted, or a bad/typo'd id) shouldn't stay indexable under a generic
    // "Listing" title — that's exactly the kind of low-value duplicate page Search Console flags.
    index: !isError,
  });

  // Product/Offer markup is what makes a listing eligible for price-carrying rich results.
  // Priced in USD because USDC is dollar-denominated — schema.org expects a fiat currency code,
  // and "USDC" is not one.
  useJsonLd(
    useMemo(
      () =>
        listing
          ? {
              "@context": "https://schema.org",
              "@type": "Product",
              name: listing.title,
              description: listing.description,
              url: `${SITE_URL}/listings/${listing.id}`,
              category: LISTING_CATEGORY_LABELS[listing.category],
              ...(listing.sellerUsername
                ? { brand: { "@type": "Brand", name: listing.sellerUsername } }
                : {}),
              offers: {
                "@type": "Offer",
                url: `${SITE_URL}/listings/${listing.id}`,
                price: formatUsdcBaseUnits(BigInt(listing.priceBaseUnits)),
                priceCurrency: "USD",
                // FILE listings have unlimited stock (a fresh download every sale). A LICENSE_KEY
                // listing is genuinely sold out at 0 remaining — reflecting that accurately
                // matters for price-carrying rich results, which can show "out of stock".
                availability:
                  listing.fulfillmentType === "LICENSE_KEY" &&
                  (listing.availableLicenseKeyCount ?? 0) < 1
                    ? "https://schema.org/OutOfStock"
                    : "https://schema.org/InStock",
                ...(listing.sellerUsername
                  ? { seller: { "@type": "Organization", name: listing.sellerUsername } }
                  : {}),
              },
            }
          : null,
      [listing],
    ),
  );

  useJsonLd(
    useMemo(
      () =>
        listing
          ? breadcrumbJsonLd([
              { name: "Browse listings", path: "/" },
              { name: listing.title, path: `/listings/${listing.id}` },
            ])
          : null,
      [listing],
    ),
  );

  if (isLoading) return <LoadingState label="Loading listing…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!listing) return null;

  const isOwnListing = isSignedIn && principal?.userId === listing.sellerId;
  const isSoldOut =
    listing.fulfillmentType === "LICENSE_KEY" && (listing.availableLicenseKeyCount ?? 0) < 1;

  async function handleMessageSeller() {
    if (!listing) return;
    setMessageError(null);
    try {
      const conversation = await startConversation.mutateAsync(listing.id);
      navigate(`/messages/${conversation.id}`);
    } catch (err) {
      setMessageError(describeError(err));
    }
  }

  return (
    <article className="stack">
      <div>
        <span className="badge badge--neutral text-sm">{LISTING_CATEGORY_LABELS[listing.category]}</span>
        {listing.accountPlatform && (
          <span className="badge badge--info text-sm">
            {ACCOUNT_PLATFORM_LABELS[listing.accountPlatform]}
          </span>
        )}
        {listing.accountStatsFetchedAt && (
          <span className="badge badge--success text-sm" title="Public stats confirmed by the platform">
            ✓ Verified
          </span>
        )}
        {listing.domainName && (
          <span className="badge badge--info text-sm">{listing.domainName}</span>
        )}
        {listing.domainOwnershipVerifiedAt && (
          <span className="badge badge--success text-sm" title="Domain ownership confirmed via DNS">
            ✓ Verified
          </span>
        )}
        <h1>{listing.title}</h1>
        <ListingCover listing={listing} className="listing-cover" />
        <p className="muted text-sm">
          {listing.sellerUsername && (
            <>
              Sold by{" "}
              <Link to={`/sellers/${listing.sellerId}`} className="seller-link">
                {listing.sellerUsername}
              </Link>{" "}
              ·{" "}
            </>
          )}
          Listed {formatDateTime(listing.createdAt)}
        </p>
        <StarRatingDisplay rating={listing.sellerRating} />
        <SellerBadges badges={listing.sellerBadges} />
      </div>

      <div className="card">
        <Money baseUnits={listing.priceBaseUnits} className="text-sm" />
        {listing.fulfillmentType === "LICENSE_KEY" && (
          <p className="text-sm muted">
            {isSoldOut
              ? "Sold out — check back later or ask the seller to restock."
              : `${listing.availableLicenseKeyCount} in stock`}
          </p>
        )}
        {listing.fulfillmentType === "ACCOUNT_TRANSFER" && (
          <p className="text-sm muted">
            One account — a guided, platform-verified handoff once you fund the order. See{" "}
            <Link to="/how-it-works">how account transfers work</Link>.
          </p>
        )}
        {listing.fulfillmentType === "DOMAIN_TRANSFER" && (
          <p className="text-sm muted">
            One domain — the seller's transfer code is held until you fund the order, then revealed
            to you. See <Link to="/how-it-works">how domain transfers work</Link>.
          </p>
        )}
        {listing.fulfillmentType === "NFT_TRANSFER" && (
          <p className="text-sm muted">
            One NFT — once you fund the order, the seller sends it directly to your own wallet; we
            never take custody of it.
          </p>
        )}
        {listing.accountStatsFetchedAt && (
          <p className="text-sm muted">
            ✓ Verified against {listing.accountPlatform ? ACCOUNT_PLATFORM_LABELS[listing.accountPlatform] : "the platform"}
            's own public stats:{" "}
            {(listing.accountStatsFollowerCount ?? 0).toLocaleString()}{" "}
            {listing.accountPlatform === "YOUTUBE" ? "subscribers" : "followers"}
            {listing.accountStatsAccountCreatedAt &&
              ` · account created ${formatDateTime(listing.accountStatsAccountCreatedAt)}`}
          </p>
        )}
        <p className="desc-full">{listing.description}</p>
      </div>

      <div className="card stack">
        <p className="text-sm muted">
          You pay the listed price plus a 10% platform fee, itemized before you sign anything.
        </p>
        {isOwnListing ? (
          <p className="muted">This is your own listing — you can&apos;t purchase it.</p>
        ) : isSoldOut ? (
          <p className="muted">This listing is sold out.</p>
        ) : isSignedIn ? (
          <>
            <Link to={`/checkout/${listing.id}`} className="btn btn-primary btn-block">
              Buy this listing
            </Link>
            <button
              type="button"
              className="btn btn-secondary btn-block"
              disabled={startConversation.isPending}
              onClick={() => void handleMessageSeller()}
            >
              {startConversation.isPending ? "Opening…" : "Message seller"}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-block"
              disabled={watchListing.isPending || watchListing.isSuccess}
              onClick={() => watchListing.mutate(listing.id)}
            >
              {watchListing.isSuccess
                ? "Added to your watchlist ✓"
                : watchListing.isPending
                  ? "Adding…"
                  : "Add to watchlist (get a price-drop alert)"}
            </button>
            {watchListing.isSuccess && (
              <p className="text-sm muted">
                Manage everything you're watching from your <Link to="/watchlist">watchlist</Link>.
              </p>
            )}
            {watchListing.isError && (
              <p className="field-error-text" role="alert">
                {describeError(watchListing.error)}
              </p>
            )}

            {!showOfferForm && !makeOffer.isSuccess && (
              <button
                type="button"
                className="btn btn-secondary btn-block"
                onClick={() => setShowOfferForm(true)}
              >
                Make an offer
              </button>
            )}
            {showOfferForm && !makeOffer.isSuccess && (
              <form className="stack" onSubmit={handleOfferSubmit}>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label htmlFor="offer-amount">Your offer (USDC)</label>
                  <input
                    id="offer-amount"
                    type="text"
                    inputMode="decimal"
                    placeholder="Below the listing price"
                    value={offerAmountInput}
                    onChange={(e) => setOfferAmountInput(e.target.value)}
                  />
                </div>
                {offerFormError && (
                  <p className="field-error-text" role="alert">
                    {offerFormError}
                  </p>
                )}
                <button type="submit" className="btn btn-primary btn-block" disabled={makeOffer.isPending}>
                  {makeOffer.isPending ? "Sending…" : "Send offer"}
                </button>
              </form>
            )}
            {makeOffer.isSuccess && (
              <p className="text-sm muted">
                Offer sent — track it on your <Link to="/offers">offers</Link> page.
              </p>
            )}
            {makeOffer.isError && (
              <p className="field-error-text" role="alert">
                {describeError(makeOffer.error)}
              </p>
            )}
            {messageError && (
              <p className="field-error-text" role="alert">
                {messageError}
              </p>
            )}
          </>
        ) : (
          <Link to="/sign-in" className="btn btn-primary btn-block">
            Sign in to buy
          </Link>
        )}
      </div>

      {reviews && reviews.items.length > 0 && (
        <div className="card stack">
          <h2>Reviews of this seller</h2>
          {reviews.items.map((review) => (
            <div key={review.id} className="stack" style={{ gap: "var(--space-1)" }}>
              <div className="row row--between">
                <StarRatingDisplay rating={{ average: review.rating, count: 1 }} count={false} size="sm" />
                <span className="text-sm muted">
                  {review.reviewerUsername ?? "Buyer"} · {formatDateTime(review.createdAt)}
                </span>
              </div>
              {review.comment && <p className="text-sm">{review.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </article>
  );
}
